/**
 * Chat Container Component
 * Main container for the chat interface
 * Matches main app's container structure (React Native)
 */

import { Banner, Platform, StyleSheet, View } from '@/components/common';
import { useTranslation } from '@/lib/i18n';
import type { ChatContainerProps } from '../../types/chat';
import { useChatStore, selectMessages, selectIsLoading, selectIsTyping, selectError, selectShowSenderNames } from '../../hooks/useChatStore';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';

export function ChatContainer({ config, style }: ChatContainerProps) {
  const { t } = useTranslation();
  const messages = useChatStore(selectMessages);
  const isLoading = useChatStore(selectIsLoading);
  const isTyping = useChatStore(selectIsTyping);
  const error = useChatStore(selectError);
  const showSenderNames = useChatStore(selectShowSenderNames);
  const isStaffDiscussion = useChatStore(state => Boolean(state.activeEducationConversation && state.activeEducationConversation.audience_type !== 'private_ai'));
  const sendMessage = useChatStore(state => state.sendMessage);

  const isEmbeddedWeb = (() => {
    try {
      if (Platform.OS !== 'web') return false;
      return window.parent && window.parent !== window;
    } catch {
      return false;
    }
  })();

  const isWeb = Platform.OS === 'web';
  const useFixedWebInput = false; // Always use flex layout for input instead of fixed positioning to fix desktop resize bug
  // Keep enough bottom spacing so the last messages aren't covered by the fixed input.
  const webInputHeightEstimate = 56;

  return (
    <View
      style={[
        styles.container,
        isWeb ? ({ height: '100%' } as any) : null,
        // In embedded (iframe) mode, use a slightly transparent app background.
        // Host design token: --bg-app = #F7F3EA
        isEmbeddedWeb ? ({ backgroundColor: 'rgba(247, 243, 234, 0.92)' } as any) : null,
        style,
      ]}
    >
      {/* Spacer to prevent mobile pull-to-refresh and scroll collision after header removal */}
      <View style={{ height: 1, backgroundColor: 'transparent' }} />

      {/* Error banner */}
      {error && (
        <Banner
          visible={!!error}
          actions={[
            {
              label: t('chat.error.dismiss', 'Dismiss'),
              onPress: () => useChatStore.getState().setError(null),
            },
          ]}
          icon="alert-circle"
        >
          {error.message}
        </Banner>
      )}

      {/* Messages */}
      <MessageList 
        messages={messages}
        isLoading={isLoading}
        isTyping={isTyping}
        showSenderNames={showSenderNames || isStaffDiscussion}
        bottomInset={useFixedWebInput ? webInputHeightEstimate : 0}
      />

      {/* Input */}
      <View style={useFixedWebInput ? styles.webFixedInput : null}>
        <MessageInput
          onSend={sendMessage}
          disabled={isLoading}
          showVoiceInput={config.features?.voiceInput}
          autoFocus={config.behavior?.autoFocus}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F7F3EA',
    position: 'relative',
  },
  webFixedInput: {
    // Use fixed positioning on web so the input is pinned to the iframe viewport
    // (absolute would pin to the container, which may not fill the iframe).
    position: 'fixed' as any,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
  },
  header: {
    alignItems: 'center',
    minHeight: 44,
    height: 44,
  },
  headerContent: {
    alignItems: 'center',
  },
  headerWebShadow: {
    // react-native-web prefers `boxShadow` vs legacy shadow* props.
    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
  },
  headerWebSticky: {
    position: 'sticky' as any,
    top: 0,
    zIndex: 20,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  headerSubtitle: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 12,
    lineHeight: 16,
    textAlign: 'center',
  },
});
